import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
    SquaresFour, MagnifyingGlass, Kanban, SignOut, Sparkle, Gear,
} from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";

const baseNav = [
    { to: "/", label: "Dashboard", icon: SquaresFour, testid: "nav-dashboard" },
    { to: "/discover", label: "Discover", icon: MagnifyingGlass, testid: "nav-discover" },
    { to: "/pipeline", label: "Pipeline", icon: Kanban, testid: "nav-pipeline" },
];

export default function Layout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const isAdmin = user?.role === "admin" || user?.role === "owner";
    const navItems = isAdmin
        ? [...baseNav, { to: "/settings", label: "Settings", icon: Gear, testid: "nav-settings" }]
        : baseNav;

    return (
        <div className="min-h-screen flex bg-background">
            {/* Sidebar */}
            <aside className="w-60 shrink-0 bg-white border-r border-border flex flex-col sticky top-0 h-screen">
                <div className="p-5 border-b border-border">
                    <img src="/scout-ai-logo.png" alt="Scout AI" className="w-full max-w-[160px] h-auto" data-testid="brand-logo" />
                </div>
                <nav className="flex-1 p-3 space-y-1">
                    {navItems.map(({ to, label, icon: Icon, testid }) => (
                        <NavLink
                            key={to}
                            to={to}
                            end={to === "/"}
                            data-testid={testid}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors ${
                                    isActive
                                        ? "bg-foreground text-white"
                                        : "text-foreground hover:bg-muted"
                                }`
                            }
                        >
                            <Icon size={18} weight="regular" />
                            <span>{label}</span>
                        </NavLink>
                    ))}
                </nav>
                <div className="p-3 border-t border-border">
                    <div className="px-3 py-2">
                        <div className="overline text-muted-foreground">Org</div>
                        <div className="text-sm font-medium truncate" data-testid="org-name">{user?.org_name}</div>
                    </div>
                    <div className="px-3 py-2">
                        <div className="overline text-muted-foreground">User</div>
                        <div className="text-sm font-medium truncate" data-testid="user-name">{user?.name}</div>
                        <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
                    </div>
                    <button
                        data-testid="logout-btn"
                        onClick={logout}
                        className="w-full mt-2 flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-sm transition-colors"
                    >
                        <SignOut size={16} />
                        Sign out
                    </button>
                </div>
            </aside>

            {/* Main */}
            <div className="flex-1 min-w-0">
                <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-border">
                    <div className="px-6 lg:px-8 h-14 flex items-center justify-between">
                        <div className="flex items-center gap-2 overline text-muted-foreground">
                            <span className="h-1.5 w-1.5 bg-signal-success rounded-full" />
                            <span>System Online</span>
                        </div>
                        <button
                            data-testid="header-discover-cta"
                            onClick={() => navigate("/discover")}
                            className="btn-ai text-sm py-1.5"
                        >
                            <Sparkle size={16} weight="fill" />
                            Discover Leads
                        </button>
                    </div>
                </header>
                <main className="px-6 lg:px-8 py-6">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
